import { ShowWithComedian, processShowData } from "@punchup/data/legacy";
import {
  useTixologiEvent,
  useTixologiWidget,
} from "@punchup/data/legacy/hooks/tixologi";

import CarouselItemImageBackdrop from "./CarouselItemImageBackdrop";
import { FastCheckoutModalOrDrawer } from "@punchup/ui-common/components/showsNearby";
import Image from "next/image";
import { TicketIcon } from "@punchup/ui-common/components/common/icons";
import {
  UserEvents,
  trackEvent,
  trackGoogleAdsConversion,
} from "@punchup/analytics";
import { type VenueCarouselTheme } from "./VenueCarousel";
import { ZapIcon } from "lucide-react";
import dayjs from "@punchup/utils/dayjs";
import {
  formatVenueShowGroupDateRange,
  multiDayEventSpan,
  nonBreakingDayRange,
} from "./venueShowGroupDate";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { showPosterUrl } from "./showPosterUrl";
import { isTixologiWidgetLink } from "@punchup/utils/tixologiWidgetOrigin";
import { useFeatureGate } from "statsig-react";
import { useState } from "react";

interface VenueShowCarouselItemProps {
  show: ShowWithComedian;
  theme: VenueCarouselTheme;
  venueLogoImg: string | null;
}

export default function VenueShowCarouselItem({
  show,
  theme,
  venueLogoImg,
}: VenueShowCarouselItemProps) {
  const tixologiWidget = useTixologiWidget();
  const tixologiEventData = useTixologiEvent(
    show.tixologi_event_id ?? undefined
  );
  const openTixologiWidget = tixologiWidget.open;
  const event = tixologiEventData.data;

  const fastCheckoutGate = useFeatureGate("fast-checkout-punchup");
  const punchupFastCheckoutEnabled = fastCheckoutGate.gate.value;

  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);

  const comedians =
    show.show_comedians.length > 0
      ? show.show_comedians
      : show.comedian
        ? [show.comedian]
        : [];

  const comedianNames = comedians
    .filter((comedian) => comedian !== null)
    .map((comedian) => comedian.display_name || "")
    .filter((name) => name !== "");

  const showDatetime = dayjs(show.datetime);
  const todayDatetime = dayjs();

  // A multi-day event (festival) shows a date range + day count instead of a
  // single date + time; its end lives on the linked Tixologi event fetched
  // above. Single-day shows are unchanged.
  const span = multiDayEventSpan(event);

  const dayLabel = todayDatetime.isSame(showDatetime, "day")
    ? "Today"
    : showDatetime.format("dddd");
  const dateLabel = span
    ? nonBreakingDayRange(
        formatVenueShowGroupDateRange(span.start, span.end, span.tz)
      )
    : showDatetime.format("MMM D");
  const detailLabel = span
    ? `${span.dayCount} days`
    : showDatetime.format("h:mma");

  const isTixologiShowUrl = isTixologiWidgetLink(show.ticket_link);

  const trackBuyTickets = () => {
    trackEvent(UserEvents.BUY_TICKETS_CLICKED, {
      VenueId: show.venue_id,
      ShowId: show.id,
      ComedianIds: comedians.map((comedian) => comedian.id),
      ComedianNames: comedians.map((comedian) => comedian.display_name),
      source: "venue_carousel",
      IsTixologi: isTixologiShowUrl,
    });

    // Track Google Ads conversion
    trackGoogleAdsConversion({
      showId: show.id,
      venueId: show.venue_id,
      comedianIds: comedians.map((comedian) => comedian.id),
    });
  };

  const handleTixologiBuyTickets = () => {
    trackBuyTickets();
    if (punchupFastCheckoutEnabled) {
      setCheckoutModalOpen(true);
    } else {
      openTixologiWidget(processShowData(show));
    }
  };

  const {
    buttonLabelColor,
    buttonBackgroundColor,
    placeholderBackgroundColor,
  } = theme;

  const imageUrl = showPosterUrl(show, event);

  const placeholderContent = (
    <div
      className="w-full h-full flex flex-row justify-center items-start py-4 md:py-8 px-6"
      style={{
        backgroundColor: placeholderBackgroundColor ?? "#000000",
      }}
    >
      {venueLogoImg ? (
        <Image
          alt="venue logo"
          src={getCDNImageURL(venueLogoImg)}
          className="h-[100px] md:h-[250px] w-auto md:max-w-md"
          width={250}
          height={250}
          sizes="(max-width: 768px) 100px, 250px"
        />
      ) : null}
    </div>
  );

  return (
    <div className="w-full flex-shrink-0 snap-center bg-white shadow-lg overflow-hidden relative md:hover:cursor-grab md:active:cursor-grabbing">
      <div className="flex h-[300px] md:h-[400px] relative">
        <CarouselItemImageBackdrop
          imageUrl={imageUrl}
          alt={show.title || "Show poster"}
          placeholderContent={placeholderContent}
          showGradient={true}
          objectFit="contain" // for show carousel items, contain the show image inside the carousel item (do not crop)
        />

        {/* Overlay - show details */}
        <div className="absolute z-30 left-6 right-6 md:right-8 bottom-6 md:left-14 md:bottom-10 flex flex-col gap-y-3 md:gap-y-6 flex-shrink-0 w-[calc(100%-3rem)] md:w-[calc(100%-7rem)]">
          <div className="flex flex-col gap-y-0 md:gap-y-0.5">
            {/* Show title or comedian names */}
            {show.title ? (
              <h3
                className="font-sans text-[1.69rem] md:text-4xl font-bold normal-case text-white line-clamp-2 [text-shadow:_1px_1px_4px_#000] select-none"
                style={{ lineHeight: 1.3 }}
              >
                {show.title}
              </h3>
            ) : (
              <h3
                className="font-sans text-2xl xs:text-3xl md:text-4xl font-bold normal-case text-white line-clamp-2 [text-shadow:_1px_1px_4px_#000] select-none"
                style={{ lineHeight: 1.3 }}
              >
                {comedianNames.join(", ")}
              </h3>
            )}

            {/* Date and time (or date range + day count for a multi-day event) */}
            <p className="text-lg md:text-xl font-medium text-white [text-shadow:_1px_1px_4px_#000] select-none">
              {dayLabel}, {dateLabel} – {detailLabel}
            </p>
          </div>

          {/* Buy tickets button - always at bottom */}
          <div className="mt-auto">
            {show.is_sold_out ? (
              <button
                type="button"
                disabled
                className="btn text-white text-sm font-semibold leading-5 flex flex-row items-center justify-center px-8 py-3"
                style={{
                  backgroundColor: buttonBackgroundColor,
                  color: buttonLabelColor,
                }}
              >
                <span className="select-none">SOLD OUT</span>
              </button>
            ) : isTixologiShowUrl ? (
              <button
                type="button"
                onClick={handleTixologiBuyTickets}
                className="btn text-white text-sm font-semibold leading-5 flex flex-row items-center justify-center px-8 py-3"
                style={{
                  backgroundColor: buttonBackgroundColor,
                  color: buttonLabelColor,
                }}
              >
                <ZapIcon className="w-4 h-4 mr-1.5" />
                <span className="select-none">Fast Checkout</span>
              </button>
            ) : (
              // External vendor: real anchor so the GA4 linker can
              // auto-decorate `_gl` onto the outbound URL on click.
              <a
                href={show.ticket_link ?? "#"}
                target="_blank"
                rel="noopener"
                onClick={trackBuyTickets}
                className="btn text-white text-sm font-semibold leading-5 flex flex-row items-center justify-center px-8 py-3"
                style={{
                  backgroundColor: buttonBackgroundColor,
                  color: buttonLabelColor,
                }}
              >
                <TicketIcon className="w-4 h-4 mr-1.5 fill-white" />
                <span className="select-none">Buy Tickets</span>
              </a>
            )}
          </div>
        </div>
      </div>

      <FastCheckoutModalOrDrawer
        show={processShowData(show)}
        comedianNames={comedianNames}
        isOpen={checkoutModalOpen}
        setIsOpen={setCheckoutModalOpen}
      />
    </div>
  );
}
